// agentBootstrap.js — S2-B phase 2: the PRE-AUTH identity/branding source (2026-09-23).
//
// ⛔ WHAT THIS REPLACES. Before a token exists (/signin, /guest, /signup, the Header's first paint,
// SEO JSON-LD, DNS routing) the SPA read the compiled src/data/{iBot,settings,system}.json. Those
// files are a stale archive (the DB is authoritative since Wave 2), they shipped every entry's
// `integration` block (SMTP credentials, Stripe keys) in the bundle, and resolveDNS defaulted any
// unknown host to Nova. Now: GET /agent-bootstrap?host=<hostname>[&id=] — botapi's PUBLIC,
// allowlisted projection (agent-bootstrap.controller.ts:20-56; Cache-Control public, max-age=60):
//   { agentId, legacyAssistantId, enabled, guestmode, branding, config }
// config ⊂ {dns, endpoint, media, welcome, persona, presets, header, iconSet, type, theme} — NO
// title, NO name, NO instructions. Post-token reads stay on botIdentity.js (B1).
//
// ⚠️ AVAILABILITY = HARD FAIL (decision A). 404 ⇒ 'not-found' (unknown host/id — NEVER Nova);
// network/5xx/garbage ⇒ 'unavailable'. No JSON fallback, no snapshot. Callers render the state.
//
// ⚠️ PLAIN JS ON PURPOSE (same reason as guestGate.js / botIdentity.js): the node tests run it.

import { projectBranding } from './botIdentity.js';

export const DEFAULT_API_BASE = 'https://robot-api-37f87b97b291.herokuapp.com';

/** User-facing text for the two pre-auth failure states (one wording, every surface). */
export const PREAUTH_TEXT = Object.freeze({
  'not-found': 'No assistant is configured for this address. Please check the link you were given.',
  unavailable: 'This assistant is unavailable right now. Please try again later.',
  'choose': 'No assistant was selected. Please open the link for the assistant you want to use.',
});

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const baseOf = (b) => String(b || DEFAULT_API_BASE).replace(/\/+$/, '');

/**
 * @param {{host?: string, id?: string}} q
 * @param {{fetchImpl?: Function, baseUrl?: string}} [deps]
 * @returns {Promise<{status:'ok', record:object} | {status:'not-found'|'unavailable', reason:string}>}
 */
export async function fetchAgentBootstrap({ host, id } = {}, { fetchImpl = globalThis.fetch, baseUrl } = {}) {
  const params = new URLSearchParams();
  if (host) params.set('host', String(host).toLowerCase());
  if (id) params.set('id', String(id));
  if (![...params.keys()].length) return { status: 'not-found', reason: 'no host and no id to resolve' };
  let res;
  try {
    res = await fetchImpl(`${baseOf(baseUrl)}/agent-bootstrap?${params}`, { headers: { Accept: 'application/json' } });
  } catch (err) {
    return { status: 'unavailable', reason: `bootstrap unreachable: ${err?.message || err}` };
  }
  if (res?.status === 404) return { status: 'not-found', reason: `no agent matches ${params}` };
  if (!res?.ok) return { status: 'unavailable', reason: `bootstrap HTTP ${res?.status}` };
  let record;
  try { record = await res.json(); } catch { return { status: 'unavailable', reason: 'bootstrap returned non-JSON' }; }
  if (!isObj(record) || !record.agentId) return { status: 'unavailable', reason: 'bootstrap record has no agentId' };
  if (record.enabled === false) return { status: 'not-found', reason: `agent ${record.agentId} is disabled` };
  return { status: 'ok', record };
}

/** The id links keep using: the legacy asst_ while it exists, else the agent id (§3.2's rule). */
export const bootstrapBotId = (record) => (record?.legacyAssistantId || record?.agentId || '');

/**
 * The OrgSettings-shaped object the pre-auth pages + Header render: the record's `branding`,
 * allowlisted through BRANDING_KEYS (B1c — never runtime, never integration, never the record),
 * plus `botid` and the guest-mode decision in the shape `type.guestmode` the pages already read.
 *
 * ⛔ TITLE PRECEDENCE IS PINNED (GB ruling 2026-09-23): `org.title` is `branding.title` — the
 * ORG-level share/header metadata (Coach G: "Guttman Development Strategies"). The PAGE/TAB
 * title is `config.title` and comes from the post-token builder (botIdentity.js), which this
 * function never touches. Do not "unify" them.
 */
export function orgFromBootstrap(record, botId) {
  if (!isObj(record)) return null;
  const brand = projectBranding(isObj(record.branding) ? record.branding : {});
  return {
    ...brand,
    id: record.agentId,
    botid: botId || bootstrapBotId(record),
    type: { ...(isObj(brand.type) ? brand.type : {}), guestmode: record.guestmode === true },
  };
}
